import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import { useUpdateUserConfig } from "../hooks/useBotFilters";
import { useRetailerConfig } from "../hooks/useRetailerConfig";
import { useStores } from "../hooks/useStores";
import { StoresFilter } from "../components/filtertypes";
import { AlertCircle, Sparkles, ChevronRight, MapPin } from "lucide-react";
import {
  extraCatalogStoreCity,
  extraCatalogStoreName,
  extraCatalogStoreStreet,
  findExtraCatalogStore,
  formatExtraStoreId,
  getExtraStores,
} from "../utils/extraStores";
import { splitExtraStores } from "../utils/hiddenExtraStores";
import { useHiddenExtraStores } from "../hooks/useHiddenExtraStores";
import { getUserStoresForRetailer } from "../config/retailers";
import { effectiveStoreSlotLimit, getRetailerAccessOverride } from "../utils/userUtils";
import { getRetailerStoreLimit, shouldPromptProStoreUpgrade } from "../utils/retailerStoreLimits";
import BotFiltersOnboardingModal from "../components/BotFiltersOnboardingModal";
import UpgradeModal from "../components/UpgradeModal";

export default function BotFiltersPage() {
  const navigate = useNavigate();
  const { user, isLoading: userLoading, load } = useUserContext();
  const { t } = useTranslation();
  const retailerConfig = useRetailerConfig();
  const updateUserConfigMutation = useUpdateUserConfig();
  const { stores: loadedStores } = useStores(retailerConfig.retailer as any);
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [dismissedStoreOnboardingFor, setDismissedStoreOnboardingFor] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  // Extra stores hidden in the store picker are left out here too.
  const { hiddenIdsFor } = useHiddenExtraStores();
  const extraStores = splitExtraStores(getExtraStores(user, retailerConfig.retailer), hiddenIdsFor(retailerConfig.retailer)).visible;

  useEffect(() => {
    setSelectedStores(getUserStoresForRetailer(user, retailerConfig.retailer));
  }, [user, retailerConfig]);

  useEffect(() => {
    if (!user || userLoading) return;
    const savedStores = getUserStoresForRetailer(user, retailerConfig.retailer);
    const needsStores =
      retailerConfig.isFeatureEnabled("stores") &&
      savedStores.length === 0;
    if (!needsStores) {
      setShowOnboardingModal(false);
      setDismissedStoreOnboardingFor(null);
      return;
    }
    setShowOnboardingModal(dismissedStoreOnboardingFor !== retailerConfig.retailer);
  }, [dismissedStoreOnboardingFor, user, userLoading, retailerConfig]);

  if (!user) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="w-14 h-14 rounded-2xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center mx-auto">
            <AlertCircle className="h-7 w-7 text-red-500" />
          </div>
          <p className="text-sm text-gray-600 dark:text-dark-300">{t("botFilters.pleaseLoginToAccess") as string}</p>
        </div>
      </div>
    );
  }

  const userRetailers = user.retailers || [];
  const accessOverride = getRetailerAccessOverride(user, retailerConfig.retailer);
  const inheritedStoreLimit = getRetailerStoreLimit(userRetailers, retailerConfig.retailer);
  const planStoreLimit = accessOverride === false
    ? 0
    : accessOverride === true
      ? Math.max(1, inheritedStoreLimit)
      : inheritedStoreLimit;
  const filled = getUserStoresForRetailer(user, retailerConfig.retailer as any).filter(Boolean).length;
  const storeLimit = effectiveStoreSlotLimit(planStoreLimit, filled, user.allowMoreStores);
  const showProUpgrade = accessOverride === undefined && shouldPromptProStoreUpgrade(userRetailers, retailerConfig.retailer);
  const isAtPlanLimit = filled >= storeLimit && storeLimit > 0;

  const openProUpgrade = () => {
    if (showProUpgrade) {
      setShowUpgradeModal(true);
      return;
    }
    navigate("/settings?tab=billing&upgradeSource=bot_filters");
  };

  const handleStoreChange = async (stores: string[]) => {
    if (!user) return;

    const updateData: Record<string, unknown> = {
      ...retailerConfig.prepareUserUpdate({ stores }),
    };

    await updateUserConfigMutation.mutateAsync(updateData);
    await load();
    setSelectedStores(stores);
  };

  return (
    <>
      <BotFiltersOnboardingModal
        isOpen={showOnboardingModal}
        onComplete={() => window.location.reload()}
        onClose={() => {
          setDismissedStoreOnboardingFor(retailerConfig.retailer);
          setShowOnboardingModal(false);
        }}
      />
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        targetPlan="pro"
        title="Upgrade to Pro"
        description="Get more store locations and unlock higher limits across retailers."
        feature="More store locations"
        ctaLabel="Upgrade"
      />
      <div className="mx-auto min-h-screen max-w-xl px-4 py-5 sm:px-6 sm:py-7 lg:px-8">
        <div className="space-y-3 sm:space-y-4">
          <section className="py-1">
            <h1 className="text-lg font-semibold tracking-tight text-gray-950 dark:text-white sm:text-xl">
              {t("botFilters.stores")}
            </h1>
          </section>

          {showProUpgrade && isAtPlanLimit && (
            <button
              type="button"
              onClick={openProUpgrade}
              className="flex w-full items-center gap-3 rounded-2xl border border-purple-200 bg-purple-50 px-4 py-3 text-left transition-colors hover:bg-purple-100 dark:border-purple-900/40 dark:bg-purple-950/30 dark:hover:bg-purple-950/50"
            >
              <Sparkles className="h-5 w-5 flex-shrink-0 text-purple-600 dark:text-purple-400" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-purple-900 dark:text-purple-100">
                  Upgrade to Pro for more stores
                </span>
                <span className="mt-0.5 block text-xs text-purple-700 dark:text-purple-300">
                  Your Plus plan includes {storeLimit} store{storeLimit === 1 ? "" : "s"} here. Pro unlocks more locations.
                </span>
              </span>
              <ChevronRight className="h-4 w-4 flex-shrink-0 text-purple-600 dark:text-purple-400" />
            </button>
          )}

          {!!updateUserConfigMutation.error && (
            <div className="flex items-center gap-2.5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 dark:border-red-900/40 dark:bg-red-900/20">
              <AlertCircle className="h-4 w-4 flex-shrink-0 text-red-500 dark:text-red-400" />
              <p className="text-sm text-red-700 dark:text-red-300">
                {(updateUserConfigMutation.error as Error)?.message || t("common.somethingWentWrong")}
              </p>
            </div>
          )}

          {storeLimit > 0 ? (
            <StoresFilter
              selectedStores={selectedStores}
              storeLimit={storeLimit}
              retailer={retailerConfig.retailer}
              loadedStores={loadedStores as any}
              onStoreChange={handleStoreChange}
            />
          ) : (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-600 dark:border-dark-600 dark:bg-dark-900/50 dark:text-dark-300">
              {t("botFilters.noStoresSelectedMessage")}
            </div>
          )}

          {extraStores.length > 0 && (
            <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-dark-700 dark:bg-dark-900">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-gray-900 dark:text-white">{t("botFilters.extraStoresTitle")}</h2>
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300">{extraStores.length}</span>
              </div>
              <p className="mt-1 text-sm text-gray-600 dark:text-dark-300">{t(retailerConfig.retailer === "walmart" ? "botFilters.extraStoresDescriptionWalmart" : "botFilters.extraStoresDescription", { count: extraStores.length })}</p>
              <ul className="mt-3 divide-y divide-gray-200/80 dark:divide-dark-700">
                {extraStores.map((extra) => {
                  const retailer = retailerConfig.retailer;
                  const id = formatExtraStoreId(extra.storeId);
                  const store = findExtraCatalogStore(loadedStores, retailer, id);
                  const storeName = extraCatalogStoreName(store, retailer);
                  const street = extraCatalogStoreStreet(store, retailer);
                  const nearCity = extraCatalogStoreCity(findExtraCatalogStore(loadedStores, retailer, extra.nearStoreId), retailer);
                  const nearId = formatExtraStoreId(extra.nearStoreId);
                  return (
                    <li key={id} className="flex items-center gap-3 py-2.5">
                      <MapPin className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-dark-400" aria-hidden="true" />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5">
                          <span className="truncate text-[15px] font-bold text-gray-900 dark:text-white">{storeName || `Store ${id}`}</span>
                          <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold leading-4 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300">{t("products.extraStoreBadge")}</span>
                        </span>
                        <span className="block truncate text-[13px] text-gray-500 dark:text-dark-300">
                          #{id}{street && street !== storeName ? ` · ${street}` : ""}
                        </span>
                        <span className="block truncate text-[12px] text-gray-400 dark:text-dark-400">
                          {nearId && nearCity
                            ? t("products.extraStoreNear", { miles: extra.distanceMiles, city: nearCity, id: nearId })
                            : t("products.extraStoreDistance", { miles: extra.distanceMiles })}
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => navigate(`/${retailerConfig.retailer}/products?storeId=${id}`)}
                        className="shrink-0 rounded-full bg-purple-50 px-3 py-1.5 text-[13px] font-bold text-primary-600 transition-colors hover:bg-purple-100 dark:bg-purple-400/15 dark:text-purple-300 dark:hover:bg-purple-400/25"
                      >
                        {t("botFilters.extraStoresView")}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </div>
      </div>
    </>
  );
}
